<template>
  <v-sheet id="addAutoTagsModal" class="th-modal modal" v-if="showAddAutoTagsPopup">
    <v-sheet class="th-dialog modal-content" :style="dragStyle" :class="dragClasses" @click.stop="">
      <!-- Header (Draggable) -->
      <header class="th-header" v-show="isViewerLoaded && isGeometryLoaded && !showProgress" @mousedown="startDrag">
        <div class="th-header__left">
          <div class="th-header__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div class="th-header__text">
            <h1 class="th-header__title">Master Tags Harvester</h1>
            <div class="th-header__status">
              <span class="th-status-dot"></span>
              <span>Ready to Harvest</span>
            </div>
          </div>
        </div>
        <button class="th-close" @click="onCloseClicked()" aria-label="Close dialog">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </header>

      <!-- Main Content -->
      <div class="th-body" v-show="isViewerLoaded && isGeometryLoaded && !showProgress">

        <!-- Primary Configuration Panel -->
        <section class="th-panel th-panel--config">
          <div class="th-panel__header">
            <span class="th-panel__number">01</span>
            <span class="th-panel__label">Configuration</span>
          </div>
          <div class="th-panel__content">
            <div class="th-field-group">
              <div class="th-field th-field--primary">
                <label class="th-label">
                  <span class="th-label__text">Title</span>
                  <span class="th-label__required">*</span>
                </label>
                <div class="th-input-wrapper">
                  <v-text-field
                    outlined
                    dense
                    hide-details
                    clearable
                    v-virtual-keyboard
                    class="th-input"
                    placeholder="Enter harvester title..."
                    v-model="tagHarvester.title"
                    :disabled="isReadOnly">
                  </v-text-field>
                </div>
              </div>
              <div class="th-field th-field--secondary">
                <label class="th-label">
                  <span class="th-label__text">Tier</span>
                </label>
                <div class="th-input-wrapper">
                  <v-select
                    dense
                    outlined
                    hide-details
                    class="th-input"
                    v-model="tagHarvester.selectedTreeLevel"
                    :items="treeLevels"
                    :disabled="isReadOnly">
                  </v-select>
                </div>
              </div>
              <v-btn
                v-if="!isReadOnly && !tagHarvester.filterOptions.length"
                @click="addNewOption"
                class="th-btn th-btn--icon th-btn--add"
                icon
                small>
                <v-icon size="18">mdi-plus</v-icon>
              </v-btn>
            </div>
          </div>
        </section>

        <!-- Filter Conditions Panel -->
        <section class="th-panel th-panel--filters" v-if="!!tagHarvester.filterOptions.length">
          <div class="th-panel__header">
            <span class="th-panel__number">02</span>
            <span class="th-panel__label">Filter Conditions</span>
            <span class="th-badge">{{ tagHarvester.filterOptions.length }}/5</span>
          </div>
          <div class="th-panel__content">
            <div class="th-filter-cards">
              <div
                v-for="(option, index) in tagHarvester.filterOptions"
                :key="index"
                class="th-filter-card"
                :class="{ 'th-filter-card--collapsed': option.isCollapsed }"
                :style="{ '--index': index }">

                <!-- Card Header with Operation Badge & Actions -->
                <div class="th-filter-card__header">
                  <div class="th-filter-card__op">
                    <v-select
                      v-if="!isReadOnly && !option.isCollapsed"
                      dense
                      hide-details
                      outlined
                      class="th-input th-input--op"
                      v-model="option.operation"
                      :items="operations">
                    </v-select>
                    <span v-else class="th-op-badge">{{ option.operation }}</span>
                  </div>

                  <!-- Collapsed Summary Chips -->
                  <div class="th-filter-card__summary" v-if="option.isCollapsed">
                    <span class="th-summary-chip" v-if="option.category">
                      <span class="th-summary-chip__label">Cat:</span>
                      <span class="th-summary-chip__value">{{ option.category }}</span>
                    </span>
                    <span class="th-summary-chip" v-if="option.property">
                      <span class="th-summary-chip__label">Prop:</span>
                      <span class="th-summary-chip__value">{{ option.property }}</span>
                    </span>
                    <span class="th-summary-chip" v-if="option.condition">
                      <span class="th-summary-chip__label">{{ option.condition }}</span>
                      <span class="th-summary-chip__value" v-if="option.value">"{{ option.value }}"</span>
                    </span>
                  </div>

                  <div class="th-filter-card__actions" v-if="!isReadOnly">
                    <button
                      v-if="index == tagHarvester.filterOptions.length - 1 && tagHarvester.filterOptions.length < 5"
                      class="th-action-btn th-action-btn--add"
                      @click="addNewFilterOption"
                      title="Add filter">
                      <v-icon x-small>mdi-plus</v-icon>
                    </button>
                    <button
                      class="th-action-btn th-action-btn--collapse"
                      @click="toggleFilterCollapse(index)"
                      :title="option.isCollapsed ? 'Expand' : 'Collapse'">
                      <v-icon x-small>{{ option.isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                    </button>
                    <button
                      class="th-action-btn th-action-btn--remove"
                      @click="removeOption(index)"
                      title="Remove filter">
                      <v-icon x-small>mdi-close</v-icon>
                    </button>
                  </div>
                </div>

                <!-- Card Body with Filter Fields (hidden when collapsed) -->
                <div class="th-filter-card__body" v-show="!option.isCollapsed">
                  <div class="th-filter-card__row">
                    <div class="th-filter-field">
                      <label class="th-filter-field__label">Category</label>
                      <v-select
                        v-if="!isReadOnly"
                        dense
                        hide-details
                        outlined
                        class="th-input th-input--mini"
                        v-model="option.category"
                        :items="Object.keys(propertiesByCategory)"
                        placeholder="Select...">
                      </v-select>
                      <span v-else class="th-readonly">{{ option.category }}</span>
                    </div>
                    <div class="th-filter-field">
                      <label class="th-filter-field__label">Property</label>
                      <v-select
                        v-if="!isReadOnly && option.category"
                        hide-details
                        dense
                        outlined
                        class="th-input th-input--mini"
                        v-model="option.property"
                        :items="propertiesByCategory[option.category] || []"
                        placeholder="Select...">
                      </v-select>
                      <span v-else class="th-readonly th-readonly--muted">{{ option.property || '—' }}</span>
                    </div>
                  </div>
                  <div class="th-filter-card__row">
                    <div class="th-filter-field">
                      <label class="th-filter-field__label">Condition</label>
                      <v-select
                        v-if="!isReadOnly"
                        dense
                        outlined
                        hide-details
                        class="th-input th-input--mini"
                        v-model="option.condition"
                        :items="conditions"
                        placeholder="Select...">
                      </v-select>
                      <span v-else class="th-readonly">{{ option.condition }}</span>
                    </div>
                    <div class="th-filter-field th-filter-field--value">
                      <label class="th-filter-field__label">Value</label>
                      <span v-if="option.condition == 'Is Not Empty'" class="th-readonly th-readonly--any">any value</span>
                      <div class="th-value-input" v-else>
                        <v-text-field
                          v-virtual-keyboard
                          dense
                          outlined
                          hide-details
                          class="th-input th-input--mini"
                          v-model="option.value"
                          :disabled="isReadOnly"
                          placeholder="Enter value...">
                        </v-text-field>
                        <button
                          class="th-case-btn"
                          :class="{ 'th-case-btn--active': option.caseSensitive }"
                          @click="toggleCaseSensitive(option)"
                          :disabled="isReadOnly"
                          :title="option.caseSensitive ? 'Case Sensitive' : 'Case Insensitive'">
                          Aa
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Search Panel -->
        <section class="th-panel th-panel--search">
          <div class="th-panel__header">
            <span class="th-panel__number">{{ tagHarvester.filterOptions.length ? '03' : '02' }}</span>
            <span class="th-panel__label">Search Configuration</span>
          </div>
          <div class="th-panel__content">
            <div class="th-search-grid">
              <div class="th-search-master">
                <label class="th-label">
                  <span class="th-label__text">Master Database</span>
                </label>
                <v-select
                  outlined
                  dense
                  hide-details
                  class="th-input"
                  v-if="!isReadOnly"
                  v-model="tagHarvester.masterType"
                  :items="masterTypes">
                  <template #selection="{ item }">
                    <div class="th-master-option">
                      <span class="th-master-icon">
                        <v-icon small>{{ getMasterIcon(item) }}</v-icon>
                      </span>
                      <span>{{ item }}</span>
                    </div>
                  </template>
                  <template #item="{ item }">
                    <div class="th-master-option">
                      <span class="th-master-icon">
                        <v-icon small>{{ getMasterIcon(item) }}</v-icon>
                      </span>
                      <span>{{ item }}</span>
                    </div>
                  </template>
                </v-select>
                <span v-else class="th-readonly">{{ tagHarvester.masterType }}</span>
              </div>

              <div class="th-search-stats">
                <div class="th-stat th-stat--success">
                  <span class="th-stat__value">{{ validNodesLength }}</span>
                  <span class="th-stat__label">Available tags</span>
                </div>
                <div class="th-stat th-stat--warning">
                  <span class="th-stat__value">{{ invalidIdsCount }}</span>
                  <span class="th-stat__label">Duplicates</span>
                </div>
              </div>

              <div class="th-search-options">
                <label class="th-checkbox">
                  <input type="checkbox" v-model="removeSlash" @change="updateTagsNames()">
                  <span class="th-checkbox__box"></span>
                  <span class="th-checkbox__label">Remove leading "/"</span>
                </label>
                <label class="th-checkbox">
                  <input type="checkbox" v-model="tagHarvester.markIgnore" @change="updateIgnoredTags($event)">
                  <span class="th-checkbox__box"></span>
                  <span class="th-checkbox__label">Mark as "Ignore"</span>
                </label>
              </div>

              <button
                class="th-btn th-btn--primary th-btn--search"
                :disabled="searchDisabled"
                @click="updateTags()">
                <span class="th-btn__text">{{ isReadOnly ? 'UPDATE' : 'SEARCH' }}</span>
                <span class="th-btn__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="M21 21l-4.35-4.35"/>
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </section>

        <!-- Matching Properties -->
        <section class="th-panel th-panel--matching">
          <div class="th-panel__header">
            <span class="th-panel__number">{{ tagHarvester.filterOptions.length ? '04' : '03' }}</span>
            <span class="th-panel__label">Matching Properties</span>
          </div>
          <div class="th-panel__content">
            <div class="th-matching-grid">
              <div class="th-field">
                <label class="th-label"><span class="th-label__text">Category</span></label>
                <v-select
                  v-if="!isReadOnly"
                  :items="Object.keys(propertiesByCategory)"
                  outlined
                  hide-details
                  dense
                  class="th-input"
                  v-model="matchingCategory">
                </v-select>
                <span v-else class="th-readonly">{{ matchingCategory }}</span>
              </div>
              <div class="th-field">
                <label class="th-label"><span class="th-label__text">Property</span></label>
                <v-select
                  v-if="!isReadOnly"
                  :items="propertiesByCategory[matchingCategory] || []"
                  outlined
                  hide-details
                  dense
                  class="th-input"
                  v-model="matchingProperty">
                </v-select>
                <span v-else class="th-readonly">{{ matchingProperty }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Preview Section -->
        <section class="th-panel th-panel--preview">
          <div class="th-preview-layout">
            <div class="th-preview-list">
              <div class="th-preview-header">
                <span class="th-preview-title">
                  Preview
                  <span class="th-preview-count" v-if="shownTagsList.length">({{ shownTagsList.length }})</span>
                </span>
                <div class="th-preview-search">
                  <v-text-field
                    outlined
                    dense
                    hide-details
                    clearable
                    v-virtual-keyboard
                    class="th-input th-input--search"
                    placeholder="Search..."
                    v-model="input">
                    <template #append>
                      <v-icon small>mdi-magnify</v-icon>
                    </template>
                  </v-text-field>
                </div>
              </div>
              <div class="th-preview-content">
                <div
                  v-if="!!shownTagsList.length"
                  id="harvester_scrollable"
                  class="th-tags-list"
                  @scroll="onScroll($event)">
                  <ForgeCard
                    v-for="(element, index) in shownTagsList"
                    :id="`equipment-${element.id}`"
                    :key="`harvester-card-${index}-${element.id}`"
                    :is_highlighted="element.id == selectedHarvesterNodeId"
                    class="th-tag-card"
                    :selectable="element"
                    :title="element.name"
                    :category="element.category"
                    :watermark="'Equipment'"
                    :watermark_color="element.getWatermarkColor()"
                    :url="tagHoverSrc"
                    :showcheckbox="false"
                    :bottomtext="element.matchedSearch"
                    @on-card-clicked="zoomToTag(element)">
                  </ForgeCard>
                </div>
                <div v-else class="th-empty">
                  <div class="th-empty__icon">
                    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5">
                      <rect x="6" y="10" width="36" height="28" rx="2"/>
                      <path d="M6 18h36"/>
                      <circle cx="12" cy="14" r="1.5" fill="currentColor"/>
                      <circle cx="18" cy="14" r="1.5" fill="currentColor"/>
                      <path d="M16 30l6-6 4 4 8-8"/>
                    </svg>
                  </div>
                  <span class="th-empty__text">No tags found</span>
                </div>
              </div>
            </div>
            <div class="th-preview-tree">
              <button class="th-btn th-btn--outline th-btn--tree" @click="toggleModelTree">
                <v-icon small class="mr-1">{{ modelStructureVisible ? 'mdi-file-tree' : 'mdi-file-tree-outline' }}</v-icon>
                {{ modelStructureVisible ? 'Hide' : 'Show' }} Model Tree
              </button>
            </div>
          </div>
        </section>
      </div>

      <!-- Footer -->
      <footer class="th-footer" v-if="isViewerLoaded && isGeometryLoaded && !showProgress">
        <button class="th-btn th-btn--ghost" @click="onCloseClicked()">
          Cancel
        </button>
        <button
          class="th-btn th-btn--primary"
          :disabled="generateDisabled"
          @click="generate()">
          <span v-if="!disabledIgnoreButton && !shownTagsList.length">Continue</span>
          <span v-else>Check Master</span>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="th-btn__arrow">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </footer>

      <!-- Results Dialog -->
      <HarvesterTagsResult v-if="showTagMasterResult" @close="onCloseClicked"></HarvesterTagsResult>

      <!-- Loading State -->
      <div v-if="!isViewerLoaded || !isGeometryLoaded || showProgress" class="th-loading">
        <div class="th-loading__content">
          <div class="th-loading__spinner">
            <svg viewBox="0 0 50 50">
              <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="80 40"/>
            </svg>
          </div>
          <span class="th-loading__text">{{ progressText || 'Initializing...' }}</span>
        </div>
      </div>

      <!-- Context Menu -->
      <ContextMenu ref="menu">
        <ul class="th-context-menu">
          <li @click="clearAllIgnored">
            <v-icon small class="mr-2">mdi-delete-sweep</v-icon>
            Clear ignored nodes
          </li>
        </ul>
      </ContextMenu>
    </v-sheet>
  </v-sheet>
</template>

<script>
  /* eslint-disable no-unused-vars, vue/no-unused-components, no-unreachable */
  import tagHoverImage from '../../../images/tagHover.png';
  import { postDataAsync } from '@/utils/mongoUtils';
  import {
    getNodeChildDbids,
    isolateForge,
    scrollToElementInContainer,
    getBBBox,
    getMiddleBBoxForGroup,
  } from '@/utils/forgeUtils';
  import { groupBy } from '@/scripts/js/forgeService';
  import { ForgeEquipment, ForgeObject } from '@/scripts/js/forgeModels';
  import $ from 'jquery';
  import HttpService from '../../../utils/httpService';
  import { $handleError } from '@/utils';
  import { assertPersistableUnitScale, assertElevationSane } from '@/utils/coordinateAuthority';
  import { createDraggableMixin } from '@/mixins/draggableMixin';
  import { log, warn, error } from '@/utils/debugLogger';
  import { API_LIMITS, LimitValidators } from '@/constants/apiLimits';

  export default {
    name: 'AddAutomaticTagsPopup',
    mixins: [
      createDraggableMixin({
        storageKey: 'add-auto-tags-popup-position',
        defaultPosition: { x: 100, y: 50 },
        dialogDimensions: { width: 520, height: 720 },
        margin: 24,
        fullscreenProperty: null
      })
    ],
    components: {
      ForgeCard: () => import('@/components/common/forgeCard.vue'),
      TagHarvesterPreview: () =>
        import('@/components/admin/components/tag-harvester-preview/tag-harvester-preview.vue'),
      LoadingPopup: () => import('@/components/common/loadingPopup.vue'),
      ContextMenu: () => import('@/components/common/context-menu.vue'),
      HarvesterTagsResult: () =>
        import('@/components/admin/popups/harvesterTagResult/harvesterTagsResult.vue'),
    },
    data() {
      return {
        timeout: null,
        treeLevels: [],
        masterTypes: ['Equipment Master', 'Pipeline Master', 'Structural Master'],
        operations: ['And', 'Or', 'Not'],
        conditions: ['Contains', 'Equals', 'Is Not Empty', 'Starts with'],
        tagsToCreate: [],
        tagHoverSrc: tagHoverImage,
        shownTagsLength: 40,
        shownTagsStartIndex: 0,
        showProgress: false,
        progressText: '',
        invalidIdsCount: 0,
        showResult: true,
        removeSlash: false,
        searchInput: '',
        currentSearchText: '',
        shownTagsList: [],
      };
    },
    methods: {
      getMasterIcon(masterType) {
        const icons = {
          'Equipment Master': 'mdi-cog-outline',
          'Pipeline Master': 'mdi-pipe',
          'Structural Master': 'mdi-domain'
        };
        return icons[masterType] || 'mdi-database';
      },
      initializeComponent() {
        this.$store.commit('SET_MUTABLE_IGNORED_TAGS_IDS', [
          ...this.$store.getters['GET_IGNORED_TAGS_IDS'],
        ]);

        if (!this.viewer || !this.viewer.model) {
          log(`❌ Viewer or Model is not loaded.`);
          return;
        }

        let instanceTree = this.viewer.model.getInstanceTree();

        if (!instanceTree) {
          log(`❌ Instance Tree is not loaded.`);
          return;
        }

        let levels = ['All Tiers'];
        for (let i = 1; i <= instanceTree.maxDepth; i++) levels.push(`Tier ${i}`);
        this.treeLevels = levels;
      },
      toggleCaseSensitive(option) {
        if (this.isReadOnly) return;
        option.caseSensitive = !option.caseSensitive;
      },
      openContextMenu(e) {
        e.preventDefault();
        this.$refs.menu?.open(e);
      },
      clearAllIgnored() {
        this.$store.dispatch({
          type: 'confirm_Forge',
          message: 'Are you sure you want to clear ignored tags?',
          callback: () => {
            this.$store.commit('SET_MUTABLE_IGNORED_TAGS_IDS', []);
          },
        });
        this.$refs.menu?.close();
      },
      updateIgnoredTags(e) {
        let ignoredTags = this.$store.getters['GET_MUTABLE_IGNORED_TAGS_IDS'];
        let tagsDbIds = this.validNodes.map((n) => n.dbId);

        if (this.tagHarvester.markIgnore) {
          this.$store.commit('SET_MUTABLE_IGNORED_TAGS_IDS', [...ignoredTags, ...tagsDbIds]);
        } else {
          this.$store.commit(
            'SET_MUTABLE_IGNORED_TAGS_IDS',
            ignoredTags.filter((t) => !tagsDbIds.includes(t)),
          );
        }
      },
      saveIgnoredTags() {
        let ignoredIds = this.$store.getters['GET_MUTABLE_IGNORED_TAGS_IDS'];

        postDataAsync(
          `api/equipments/updateIgnoredDbIds`,
          { assetId: this.$store.state.assetId, ignoredDbIds: ignoredIds.join(',') },
          (_) => {
            this.$store.commit('SET_IGNORED_TAGS_IDS', ignoredIds);
          },
        );
      },
      getNodesAtDepth(rootId, instanceTree, depth) {
        log(`⚠️ Get Nodes at Depth: ${depth}`);
        const nodes = [];
        function traverse(nodeId, currentDepth) {
          if (depth == 0 || currentDepth === depth) {
            nodes.push(nodeId);
          }

          if (depth == 0 || currentDepth < depth) {
            let children = getNodeChildDbids(nodeId, instanceTree);
            children.forEach((child) => traverse(child, currentDepth + 1));
          }
        }
        traverse(rootId, 0);
        log(`✅ Nodes at Depth: ${depth} - ${nodes.length}`);
        return nodes;
      },
      addNewOption() {
        this.tagHarvester.filterOptions.push({
          operation: 'And',
          category: '',
          property: '',
          condition: '',
          value: '',
          caseSensitive: false,
          isCollapsed: false,
        });
      },
      addNewFilterOption() {
        // Collapse all existing filters before adding new one
        this.tagHarvester.filterOptions.forEach(option => {
          this.$set(option, 'isCollapsed', true);
        });
        // Add new filter (expanded)
        this.tagHarvester.filterOptions.push({
          operation: 'And',
          category: '',
          property: '',
          condition: '',
          value: '',
          caseSensitive: false,
          isCollapsed: false,
        });
      },
      toggleFilterCollapse(index) {
        const option = this.tagHarvester.filterOptions[index];
        this.$set(option, 'isCollapsed', !option.isCollapsed);
      },
      removeOption(index) {
        this.tagHarvester.filterOptions.splice(index, 1);
      },
      isValidValue(condition, value, input, caseSensitive = false) {
        if (condition === 'Is Not Empty') {
          return value !== null && value !== undefined && value !== '';
        }

        let i = caseSensitive ? '' : 'i';

        switch (condition) {
          case 'Contains':
            return new RegExp(input.replace(/\?/g, '.'), i).test(value);
          case 'Equals':
            return new RegExp(`^${input.replace(/\?/g, '.')}$`, i).test(value);
          case 'Starts with':
            return new RegExp(`^${input.replace(/\?/g, '.')}`, i).test(value);
          default:
            return false;
        }
      },
      onScroll($event) {
        var elem = $event.target;
        if (elem.scrollHeight - elem.scrollTop < 0.1 * elem.scrollHeight) {
          this.shownTagsLength += 50;
        }
      },
      zoomToTag(element) {
        if (!element.children || element.children.length === 0) {
          this.$dialog.notify.warning('Tag is not matching any 3d object.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        if (this.viewer) {
          const dbIds = element.children.map(c => c.dbId);
          isolateForge(this.viewer, this.viewer.model, dbIds);
          this.$store.dispatch('updateSelectedNode', { nodeId: dbIds[0] });

          if (this.selectedHarvesterNode) {
            this.selectedHarvesterNode.isSelected = false;
            this.$store.commit('SET_HARVESTER_NODE', null);
          }
        }
      },
      async updateTags() {
        this.adminAppContainer.open3dViewerIfNot();

        this.clearFilter();

        this.scrollToTop();

        this.tagsToCreate = [];
        this.invalidIdsCount = 0;

        if (!this.viewer || !this.viewer.model || !this.viewer.model.getInstanceTree()) return;

        this.progressText = 'Scanning model structure...';
        this.showProgress = true;

        await this.clearData();

        this.tagHarvester.filterOptions = this.tagHarvester.filterOptions.filter(
          (o) => !!o.category && !!o.property && !!o.condition,
        );

        const instanceTree = this.viewer.model.getInstanceTree();
        var rootId = instanceTree.getRootId();
        let level = this.treeLevels.indexOf(this.tagHarvester.selectedTreeLevel);

        let children = this.getNodesAtDepth(rootId, instanceTree, level) ?? [];

        this.updateIgnoredNode().then(() => {
          this.validateIgnoredNodes(children)
            .then(this.validateNodesProperties)
            .then(this.validateDuplicatedNodes)
            .then((validNodes) => {
              log(`✅ Valid Nodes: ${validNodes.length}`);

              this.validNodes = validNodes;
              this.updateTagsNames();

              if (this.tagHarvester.markIgnore) {
                let ignoredTags = this.$store.getters['GET_IGNORED_TAGS_IDS'];
                let tagsDbIds = this.validNodes.length ? this.validNodes.map((n) => n.dbId) : [];
                this.$store.commit('SET_MUTABLE_IGNORED_TAGS_IDS', [...ignoredTags, ...tagsDbIds]);
                log(
                  `✅ Ignored Tags: ${this.$store.getters.GET_MUTABLE_IGNORED_TAGS_IDS.length}`,
                );
              }

              this.shownTagsLength = 50;
              this.showProgress = false;
            });
        });
      },
      updateIgnoredNode() {
        return new Promise((resolve, reject) => {
          let ignoredIds = this.$store.getters['GET_MUTABLE_IGNORED_TAGS_IDS'];

          HttpService.post({
            url: 'api/equipments/updateIgnoredDbIds',
            data: {
              assetId: this.$store.state.assetId,
              ignoredDbIds: ignoredIds.join(','),
            },
          })
            .then(() => {
              this.$store.commit('SET_IGNORED_TAGS_IDS', ignoredIds);
              resolve();
            })
            .catch(reject);
        });
      },
      scrollToTop() {
        let scrollable = document.getElementById('harvester_scrollable');
        if (scrollable) scrollable.scrollTop = 0;
      },
      getValidNodesNames(nodesIds) {
        return new Promise((resolve) => {
          log('🔎 Get Valid Nodes Names');

          let GetMatchingValue = (properties, name) => {
            if (this.matchingCategory && this.matchingProperty) {
              return (
                properties.find(
                  (p) =>
                    p.displayCategory == this.matchingCategory &&
                    p.displayName == this.matchingProperty,
                )?.displayValue ?? ''
              );
            }

            return name;
          };

          const instanceTree = this.viewer.model.getInstanceTree();

          let promises = [];
          let data = [];

          log('getProperties');

          let property = this.matchingProperty ? this.matchingProperty : 'Name'
          let monikerProperty = 'SP3d Moniker';

          let monikerCategory = 'SmartPlant 3D';

          let options = {
            propFilter: [property, monikerProperty],
            categoryFilter: this.matchingCategory ? [this.matchingCategory, monikerCategory] : ['Item', monikerCategory],
          };

          this.viewer.model.getBulkProperties(nodesIds, options, (result) => {
            for (let i = 0; i < result.length; i++) {
              let { dbId, properties } = result[i];

              let name = instanceTree.getNodeName(dbId);

              let propertyValue = properties.find((p) => p.displayName == property)?.displayValue;

              if (!propertyValue) continue;

              let monikerValue = properties.find((p) => p.displayName == monikerProperty)?.displayValue ?? '';

              let children = dbId;

              data.push({
                dbId: children,
                name: name,
                originalName: name,
                matchingValue: propertyValue.toString().trim(),
                monikerValue: monikerValue.toString().trim()
              });
            }

            resolve(data);
          });
        });
      },
      validateNodesProperties(children) {
        return new Promise((resolve) => {
          log('🔎 Validate Nodes');

          if (!this.tagHarvester.filterOptions?.length) {
            this.getValidNodesNames(children).then(resolve);
            return;
          }

          const optionsByOperation = groupBy(this.tagHarvester.filterOptions, 'operation');
          const optionsOr = optionsByOperation['Or'] ?? [];
          const optionsAnd = optionsByOperation['And'] ?? [];
          const optionsNot = optionsByOperation['Not'] ?? [];

          const properties = [];
          const categories = [];

          this.tagHarvester.filterOptions.forEach((o) => {
            if (!categories.includes(o.category)) categories.push(o.category);
            if (!properties.includes(o.property)) properties.push(o.property);
          });

          if (this.matchingCategory && this.matchingProperty) {
            if (!categories.includes(this.matchingCategory)) {
              categories.push(this.matchingCategory);
            }
            if (!properties.includes(this.matchingProperty)) {
              properties.push(this.matchingProperty);
            }
          }

          const options = {
            propFilter: properties.length ? properties : ['Name'],
            categoryFilter: categories,
          };

          const instanceTree = this.viewer.model.getInstanceTree();

          this.viewer.model.getBulkProperties(children, options, (result) => {
            const validNodes = result.reduce((acc, { dbId, properties }) => {
              if (this.isPropertiesHaveValidValue(properties, optionsOr, optionsAnd, optionsNot)) {
                const name = instanceTree.getNodeName(dbId);
                const matchingValue = this.matchingCategory && this.matchingProperty
                  ? properties.find((p) =>
                      p.displayCategory === this.matchingCategory &&
                      p.displayName === this.matchingProperty
                    )?.displayValue
                  : name;

                if (matchingValue) {
                  acc.push({
                    dbId,
                    name,
                    originalName: name,
                    matchingValue,
                  });
                }
              }
              return acc;
            }, []);

            resolve(validNodes);
          });
        });
      },
      validateIgnoredNodes(nodes) {
        return this.$store.dispatch('VALIDATE_IGNORED_NODE_IDS', { nodesIds: nodes });
      },
      validateDuplicatedNodes(nodes) {
        return this.$store
          .dispatch('VALIDATE_DUPLICATED_NODE_IDS', { nodesIds: nodes.map(n => n.dbId) })
          .then((validNodesIds) => {
            this.invalidIdsCount = nodes.length - validNodesIds.length;
            return nodes.filter((n) => validNodesIds.includes(n.dbId));
          });
      },
      isPropertiesHaveValidValue(properties, optionsOr, optionsAnd, optionsNot) {
        for (let i = 0; i < optionsNot.length; i++) {
          const statement = optionsNot[i];

          let property = properties.find(
            (p) => p.displayName == statement.property && p.displayCategory == statement.category,
          );

          if (!property?.displayValue) return false;

          let isValid = this.isValidValue(
            statement.condition,
            property.displayValue,
            statement.value,
            statement.caseSensitive,
          );

          if (isValid) return false;
        }

        if (!optionsOr.length && !optionsAnd.length) return true;

        for (let i = 0; i < optionsOr.length; i++) {
          const statement = optionsOr[i];

          let property = properties.find(
            (p) => p.displayName == statement.property && p.displayCategory == statement.category,
          );

          if (!property?.displayValue) continue;

          let isValid = this.isValidValue(
            statement.condition,
            property.displayValue,
            statement.value,
            statement.caseSensitive,
          );

          if (isValid) return isValid;
        }

        for (let i = 0; i < optionsAnd.length; i++) {
          const statement = optionsAnd[i];

          let property = properties.find(
            (p) => p.displayName == statement.property && p.displayCategory == statement.category,
          );

          if (!property?.displayValue) return false;

          let isValid = this.isValidValue(
            statement.condition,
            property.displayValue,
            statement.value,
            statement.caseSensitive,
          );

          if (!isValid) return false;

          if (i == optionsAnd.length - 1) return true;
        }

        return false;
      },
      getAllAutomaticTagsDbids(callback) {
        this.showProgress = true;
        this.$store.dispatch('fetchAllAutomaticTagsDbids', {
          assetId: this.$store.state.assetId,
          callback: () => {
            this.showProgress = false;
            if (callback) callback();
          },
        });
      },
      dbidsToAutomaticTags(dbIds) {
        const instanceTree = this.viewer.model.getInstanceTree();
        var result = [];
        dbIds.forEach((dbId) => {
          let tag = new ForgeEquipment();
          tag.originalName = instanceTree.getNodeName(dbId);
          tag.name = tag.originalName;
          tag.id = dbId;
          result.push(tag);
        });

        return result;
      },
      updateTagsNames() {
        this.shownTagsList.forEach((o) => {
          if (this.removeSlash && o.originalName.startsWith('/'))
            o.name = o.originalName.slice(1, o.originalName.length);
          else o.name = o.originalName;
        });
      },
      onCloseClicked() {
        log('🎯 Close Clicked');

        if (this.showProgress) return;

        this.validNodes = [];
        this.validNodesIds = [];
        this.shownTagsList = [];
        this.$store.commit('SET_HARVESTER_CATEGORIES', [
          'available-tag',
          'filtered-tag',
          'created-tag',
          'ignored-tag',
        ]);
        this.$store.commit('SET_HARVESTER_NODE', null);
        this.$store.dispatch('RESET_HARVESTER_INFO');
        this.tagsToCreate = [];
        this.$store.state.showAddAutoTagsPopup = false;
        this.showTagMasterResult = false;
        this.progressText = '';
        this.invalidIdsCount = 0;
      },
      generate() {
        if (!this.tagHarvester.title) {
          this.$dialog.notify.warning('Please add a Type.', {
            position: 'top-right',
            timeout: 3000,
          });
          return false;
        }

        if (!this.tagHarvester.masterType) {
          this.$dialog.notify.warning('Please select the Master.', {
            position: 'top-right',
            timeout: 3000,
          });
          return false;
        }

        if (!this.isMasterAvailable) {
          this.$dialog.notify.warning('Please update the Master table.', {
            position: 'top-right',
            timeout: 3000,
          });
          return false;
        }

        this.showTagMasterResult = true;
      },
      generate2() {
        if (!this.shownTagsList.length && this.disabledIgnoreButton) {
          this.$dialog.notify.warning(
            'No equipments found, please change the options first to find equipments.',
            {
              position: 'top-right',
              timeout: 3000,
            },
          );
          return false;
        }

        if (!this.tagHarvester.title) {
          this.$dialog.notify.warning('Please add a title.', {
            position: 'top-right',
            timeout: 3000,
          });
          return false;
        }

        this.progressText = `Updating Ignored Nodes`;
        this.showProgress = true;

        let ignoredIds = this.$store.getters['GET_MUTABLE_IGNORED_TAGS_IDS'];

        HttpService.post({
          url: 'api/equipments/updateIgnoredDbIds',
          data: {
            assetId: this.$store.state.assetId,
            ignoredDbIds: ignoredIds.join(','),
          },
        })
          .then(() => {
            this.$store.commit('SET_IGNORED_TAGS_IDS', ignoredIds);

            if (this.tagHarvester.markIgnore || this.validNodes.length === 0) {
              this.showProgress = false;
              this.onCloseClicked();
              return;
            }

            // Validate bulk tag limit
            const bulkValidation = LimitValidators.validateBulkTags(this.validNodes);
            if (!bulkValidation.isValid && bulkValidation.batchCount > 1) {
              log(`⚠️ Large batch: ${bulkValidation.message}`);
            }

            // Coordinate-space gate: refuse to persist with an unknown unit scale,
            // and sanity-check a sample conversion against the facility levels —
            // catches the silent wrong-scale case that stored raw-mm centers.
            try {
              assertPersistableUnitScale(this.viewer.model);
              const sample = this.forgeService.view3dToWorld(
                getMiddleBBoxForGroup([this.validNodes[0].dbId]).getCenter(),
              );
              assertElevationSane(sample);
              log('[Harvest] coordinate-space gate passed, sample point:', sample);
            } catch (err) {
              this.showProgress = false;
              error('[Harvest] blocked by coordinate-space gate:', err.message);
              $handleError({ message: err.message });
              return;
            }

            this.showProgress = true;
            const batchSize = API_LIMITS.MAX_BULK_TAGS;
            const validNodes = this.validNodes;

            let chain = Promise.resolve();

            for (let i = 0; i < validNodes.length; i += batchSize) {
              const batch = validNodes.slice(i, i + batchSize);
              chain = chain.then(() => {
                this.progressText = `Uploading ${i + batch.length} / ${
                  validNodes.length
                } Equipments, please wait...`;
                return this.createAutomaticEquipmentsBatch(batch);
              });
            }

            return chain;
          })
          .then(() => {
            this.$store.dispatch('fetchAllAutomaticTagsDbids', {
              assetId: this.$store.state.assetId,
            });
            this.$store.dispatch('FETCH_ADMIN_AUTOMATIC_TAGS', {
              assetId: this.$store.state.assetId,
            });
            this.showProgress = false;
            this.onCloseClicked();
          })
          .catch((err) => {
            error(err);
            this.showProgress = false;
            $handleError({
              message: err.response?.data?.message || 'An error occurred',
            });
          });
      },
      createAutomaticEquipmentsBatch(batch) {
        return HttpService.post({
          url: 'api/equipments/createAutomaticEquipments',
          data: {
            assetId: this.$store.state.assetId,
            harvester: {
              id: this.tagHarvester.id,
              title: this.tagHarvester.title,
              masterType: this.tagHarvester.masterType,
              forgeAssetId: this.$store.state.assetId,
              metaData: JSON.stringify({
                filterOptions: this.tagHarvester.filterOptions,
                selectedTreeLevel: this.tagHarvester.selectedTreeLevel,
                removeSlash: this.tagHarvester.removeSlash,
                matchingCategory: this.matchingCategory,
                matchingProperty: this.matchingProperty,
              }),
            },
            tags: batch.map(({ dbId, name }) => {
              let tag = new ForgeEquipment();

              tag.id = null;
              tag.createDate = new Date();
              tag.createdBy = this.$store.state.username;
              tag.createdById = this.$store.getters['ACCOUNT/GET_USER'].id;

              tag.name = name;
              tag.point = this.forgeService.view3dToWorld(
                getMiddleBBoxForGroup([dbId]).getCenter(),
              );
              tag.children = [];

              var elem = new ForgeObject();
              elem.dbId = dbId;
              elem.center = this.forgeService.view3dToWorld(
                getBBBox([dbId], this.viewer.model).getCenter(),
              );
              elem.name = tag.name;
              tag.children.push(elem);

              return tag;
            }),
          },
        }).then((response) => {
          if (this.tagHarvester.id == null && response.data && response.data.tagHarvester) {
            this.tagHarvester.id = response.data.tagHarvester.id;
          }

          return response;
        });
      },
      debounceSearch(val) {
        this.maxSearchLimit = 50;
        this.currentSearchText = val;
      },
      clearFilter() {
        this.maxSearchLimit = 50;
        this.searchInput = '';
        this.currentSearchText = '';
      },
      async clearData() {
        await new Promise(resolve => setTimeout(resolve, 100));

        this.tagsToCreate = [];
        this.shownTagsList = [];
        this.validNodes = [];
        this.validNodesIds = [];
        this.$store.commit('SET_HARVESTER_CATEGORIES', [
          'available-tag',
          'filtered-tag',
          'created-tag',
          'ignored-tag',
        ]);

        await new Promise(resolve => setTimeout(resolve, 50));
      },
      toggleModelTree() {
        if (!this.viewer) return;
        const visible = this.viewer.modelstructure?.visible;
        this.viewer.modelstructure?.setVisible(!visible);
      },
      toggleCategory(category) {
        let c = this.selectedCategories;

        if (c.includes(category)) {
          c = c.filter((a) => a != category);
        } else {
          c.push(category);
        }

        this.selectedCategories = c;
      },
    },
    watch: {
      showAddAutoTagsPopup: {
        handler: function (isShown) {
          $('.left-content').css('overflow-y', isShown ? 'hidden' : 'auto');
          if (isShown) {
            document.getElementsByClassName('left-content').item(0).scrollTop = 0;

            // Center dialog if no saved position (first open)
            if (!this.loadDragPosition()) {
              this.centerDragPosition();
            }

            this.initializeComponent();
            this.getAllAutomaticTagsDbids(() => {
              this.adminAppContainer.open3dViewerIfNot();
            });
          }
        },
        deep: true,
      },
      isGeometryLoaded: {
        handler: function (loaded) {
          if (loaded) {
            this.initializeComponent();
          } else {
            this.onCloseClicked();
          }
        },
        deep: true,
      },
      isViewerLoaded: {
        handler: function (loaded) {
          if (loaded) {
            this.initializeComponent();
          } else {
            this.onCloseClicked();
          }
        },
        deep: true,
      },
      selectedHarvesterNodeId: {
        handler: function (nodeId) {
          if (!nodeId) return;

          let index = this.validNodes.findIndex((t) => t.dbId == nodeId);

          isolateForge(this.viewer, this.viewer.model, [nodeId]);

          let t = this.validNodes[index];

          this.maxSearchLimit = 50;
          this.searchInput = t.name;
          this.currentSearchText = t.name;

          setTimeout(() => {
            const element = document.getElementById(`equipment-${nodeId}`);
            scrollToElementInContainer(element, '.scrollable-content');
          }, 100);
        },
        deep: true,
      },
      shownNodesIds: {
        handler: function (dbIds) {
          if (!this.viewer?.model) return;
          let instanceTree = this.viewer.model.getInstanceTree();
          if (!instanceTree) return;
          this.shownTagsList = dbIds.map((dbId) => {
            let tag = new ForgeEquipment();
            tag.originalName = instanceTree.getNodeName(dbId);
            tag.name = tag.originalName;
            tag.point = this.forgeService.view3dToWorld(getMiddleBBoxForGroup([dbId]).getCenter());
            tag.id = dbId;
            tag.children = [];

            var elem = new ForgeObject();
            elem.dbId = dbId;
            elem.center = this.forgeService.view3dToWorld(
              getBBBox([dbId], this.viewer.model).getCenter(),
            );
            elem.name = tag.name;

            tag.children.push(elem);

            return tag;
          });
        },
        deep: true,
      },
      matchingCategory: {
        handler: function (category) {
          let properties = this.propertiesByCategory[category] ?? [];

          this.matchingProperty = properties.length ? properties[0] : '';
        },
        deep: true,
        immediate: true,
      },
    },
    computed: {
      modelStructureVisible() {
        return !!this.viewer?.modelstructure?.visible;
      },
      tagHarvester() {
        return this.$store.getters['GET_TAG_HARVESTER_INFO'];
      },
      isReadOnly() {
        return !!this.tagHarvester.id;
      },
      propertiesByCategory() {
        return this.$store.getters['getAllPropertiesByCategory'];
      },
      showAddAutoTagsPopup() {
        return this.$store.state.showAddAutoTagsPopup;
      },
      isViewerLoaded() {
        return this.$store.getters['getIsTreeLoaded'];
      },
      sortedTags() {
        return [...this.tagsToCreate].sort((a, b) => a.name.localeCompare(b.name));
      },
      filteredTags() {
        return this.sortedTags.filter((t) =>
          t.name.toLowerCase().includes(this.currentSearchText.toLowerCase()),
        );
      },
      shownTags() {
        return this.tagHarvester.markIgnore
          ? []
          : this.filteredTags.slice(this.shownTagsStartIndex, this.shownTagsLength) ?? [];
      },
      modelElementsObject: {
        set(value) {
          this.$store.commit('setModelElementsObject', value);
        },
        get() {
          return this.$store.getters['getModelElementsObject'];
        },
      },
      selectedHarvesterNode() {
        return this.$store.getters['GET_HARVESTER_NODE'];
      },
      selectedHarvesterNodeId() {
        return this.selectedHarvesterNode?.nodeId;
      },
      isMasterAvailable() {
        switch (this.tagHarvester.masterType) {
          case 'Equipment Master':
            return this.$store.getters.GET_IS_EQUIPMENT_MASTER_AVAILABLE;
          case 'Pipeline Master':
            return this.$store.getters.GET_IS_PIPELINE_MASTER_AVAILABLE;
          case 'Structural Master':
            return this.$store.getters.GET_IS_STRUCTURAL_MASTER_AVAILABLE;
          default:
            return false;
        }
      },
      input: {
        get() {
          return this.searchInput;
        },
        set(val) {
          this.searchInput = val;

          if (this.timeout) clearTimeout(this.timeout);

          this.timeout = setTimeout(() => {
            this.debounceSearch(val);
          }, 500);
        },
      },
      selectedCategories: {
        get() {
          return this.$store.getters['GET_HARVESTER_CATEGORIES'];
        },
        set(value) {
          this.$store.commit('SET_HARVESTER_CATEGORIES', value);
        },
      },
      validOptions() {
        return this.tagHarvester.filterOptions.filter(
          (o) =>
            !!o.category &&
            !!o.property &&
            !!o.condition &&
            (o.condition == 'Is Not Empty' || !!o.value),
        );
      },
      disabledIgnoreButton() {
        return (
          JSON.stringify(this.$store.getters['GET_MUTABLE_IGNORED_TAGS_IDS']) ==
          JSON.stringify(this.$store.getters['GET_IGNORED_TAGS_IDS'])
        );
      },
      searchDisabled() {
        return false;
      },
      generateDisabled() {
        return (
          !this.tagHarvester.masterType ||
          !this.tagHarvester.title ||
          this.validOptions.length != this.tagHarvester.filterOptions.length
        );
      },
      forgeService: {
        get() {
          return this.$store.state.forgeService;
        },
      },
      adminAppContainer: {
        get() {
          return this.$store.state.adminAppContainer;
        },
      },
      viewer: {
        get() {
          return this.$store.state.viewer;
        },
      },
      isGeometryLoaded: {
        get() {
          return this.$store.state.isGeometryLoaded;
        },
      },
      searchLower() {
        return this.currentSearchText.toLowerCase();
      },
      filteredNodes() {
        return this.searchLower
          ? this.validNodes.filter((t) => t.name.toLowerCase().includes(this.searchLower))
          : this.validNodes;
      },
      validNodesLength: {
        get() {
          return this.validNodes.length;
        },
      },
      validNodes: {
        get() {
          return this.$store.getters.GET_HARVESTER_VALID_NODE;
        },
        set(value) {
          this.$store.commit('SET_HARVESTER_VALID_NODE', value);
        },
      },
      shownNodesIds: {
        get() {
          return (
            this.filteredNodes
              .slice(this.shownTagsStartIndex, this.shownTagsLength)
              .map((n) => n.dbId) ?? []
          );
        },
      },
      showTagMasterResult: {
        get() {
          return this.$store.getters.GET_SHOW_TAG_MASTER_RESULT;
        },
        set(value) {
          this.$store.commit('SET_SHOW_TAG_MASTER_RESULT', value);
        },
      },
      matchingCategory: {
        get() {
          return this.tagHarvester.matchingCategory;
        },
        set(value) {
          this.tagHarvester.matchingCategory = value;
        },
      },
      matchingProperty: {
        get() {
          return this.tagHarvester.matchingProperty;
        },
        set(value) {
          this.tagHarvester.matchingProperty = value;
        },
      },
    },
    mounted() {
      this.shownTagsList = [];
    },
  };
</script>

<style lang="scss">
/* =============================================================================
   TAG HARVESTER - PRECISION ENGINEERING AESTHETIC
   Inspired by aerospace control panels & technical blueprints
   ============================================================================= */

/* Import distinctive font */
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */

.th-modal {
  /* Typography */
  --th-font-primary: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  --th-font-mono: 'JetBrains Mono', 'Fira Code', monospace;

  /* Light Mode Colors */
  --th-bg-base: #fafbfc;
  --th-bg-elevated: #ffffff;
  --th-bg-sunken: #f4f5f7;
  --th-bg-hover: #f0f1f3;

  --th-border-subtle: #e8eaed;
  --th-border-default: #d1d5db;
  --th-border-strong: #9ca3af;

  --th-text-primary: #111827;
  --th-text-secondary: #4b5563;
  --th-text-muted: #9ca3af;
  --th-text-inverse: #ffffff;

  /* Accent colors - uses Vuetify app primary */
  --th-accent: var(--v-primary-base);

  --th-success: #10b981;
  --th-warning: #f59e0b;
  --th-error: #ef4444;

  --th-shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.04);
  --th-shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.06);
  --th-shadow-md: 0 4px 16px rgba(0, 0, 0, 0.08);
  --th-shadow-lg: 0 12px 40px rgba(0, 0, 0, 0.12);

  --th-radius-sm: 4px;
  --th-radius-md: 6px;
  --th-radius-lg: 10px;

  --th-transition-fast: 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  --th-transition-base: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  --th-transition-slow: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Dark Mode */
.theme--dark .th-modal {
  --th-bg-base: #0f1419;
  --th-bg-elevated: #1a1f26;
  --th-bg-sunken: #0a0d10;
  --th-bg-hover: #242b35;

  --th-border-subtle: #2d3640;
  --th-border-default: #3d4654;
  --th-border-strong: #5c6775;

  --th-text-primary: #f3f4f6;
  --th-text-secondary: #9ca3af;
  --th-text-muted: #6b7280;

  --th-shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.25);
  --th-shadow-md: 0 4px 16px rgba(0, 0, 0, 0.35);
  --th-shadow-lg: 0 12px 40px rgba(0, 0, 0, 0.5);
}

/* ═══════════════════════════════════════════════════════════════════════════
   MODAL CONTAINER
   ═══════════════════════════════════════════════════════════════════════════ */

#addAutoTagsModal.th-modal {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  font-family: var(--th-font-primary);
}

#addAutoTagsModal.th-modal .th-dialog.modal-content {
  position: relative;
  min-width: 520px;
  max-width: calc(100% - 32px);
  height: -webkit-fill-available;
  max-height: min(720px, calc(100vh - 48px));
  padding: 0;
  margin: 0;
  background: var(--th-bg-elevated);
  border: 1px solid var(--th-border-subtle);
  border-radius: var(--th-radius-lg);
  box-shadow: var(--th-shadow-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: th-dialog-enter 0.25s var(--th-transition-base);
}

@keyframes th-dialog-enter {
  from {
    opacity: 0;
    transform: translateY(-12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   HEADER
   ═══════════════════════════════════════════════════════════════════════════ */

#addAutoTagsModal .th-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--th-bg-sunken);
  border-bottom: 1px solid var(--th-border-subtle);
  /* Drag handle styling */
  cursor: grab;
  user-select: none;
}

#addAutoTagsModal .th-header:active {
  cursor: grabbing;
}

/* Dragging state applied by mixin */
#addAutoTagsModal .th-dialog.dragging {
  user-select: none;
}

#addAutoTagsModal .th-dialog.dragging .th-header {
  cursor: grabbing;
}

#addAutoTagsModal .th-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

#addAutoTagsModal .th-header__icon {
  width: 40px;
  height: 40px;
  background: var(--th-accent);
  border-radius: var(--th-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px color-mix(in srgb, var(--th-accent) 20%, transparent);
}

#addAutoTagsModal .th-header__icon svg {
  width: 22px;
  height: 22px;
  color: white;
}

#addAutoTagsModal .th-header__title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--th-text-primary);
  letter-spacing: -0.02em;
}

#addAutoTagsModal .th-header__status {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 11px;
  color: var(--th-text-muted);
  font-family: var(--th-font-mono);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

#addAutoTagsModal .th-status-dot {
  width: 6px;
  height: 6px;
  background: var(--th-success);
  border-radius: 50%;
  animation: th-pulse 2s ease-in-out infinite;
}

@keyframes th-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

#addAutoTagsModal .th-close {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: var(--th-radius-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--th-text-muted);
  transition: all var(--th-transition-fast);
}

#addAutoTagsModal .th-close svg {
  width: 18px;
  height: 18px;
}

#addAutoTagsModal .th-close:hover {
  background: var(--th-bg-hover);
  color: var(--th-error);
}

/* ═══════════════════════════════════════════════════════════════════════════
   BODY
   ═══════════════════════════════════════════════════════════════════════════ */

#addAutoTagsModal .th-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: auto;
  min-height: 0;
}

#addAutoTagsModal .th-body::-webkit-scrollbar {
  width: 6px;
}

#addAutoTagsModal .th-body::-webkit-scrollbar-track {
  background: transparent;
}

#addAutoTagsModal .th-body::-webkit-scrollbar-thumb {
  background: var(--th-border-default);
  border-radius: 3px;
}

#addAutoTagsModal .th-body::-webkit-scrollbar-thumb:hover {
  background: var(--th-accent);
}

/* ═══════════════════════════════════════════════════════════════════════════
   PANELS
   ═══════════════════════════════════════════════════════════════════════════ */

#addAutoTagsModal .th-panel {
  background: var(--th-bg-base);
  border: 1px solid var(--th-border-subtle);
  border-radius: var(--th-radius-md);
  overflow: visible;
}

#addAutoTagsModal .th-panel__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: var(--th-bg-sunken);
  border-bottom: 1px solid var(--th-border-subtle);
}

#addAutoTagsModal .th-panel__content {
  display: block;
  padding: 14px;
  visibility: visible;
  opacity: 1;
  height: auto;
  overflow: visible;
}

.th-panel__number {
  font-family: var(--th-font-mono);
  font-size: 10px;
  font-weight: 500;
  color: var(--th-accent);
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
  padding: 2px 6px;
  border-radius: 3px;
  letter-spacing: 0.02em;
}

.th-panel__label {
  font-size: 11px;
  font-weight: 600;
  color: var(--th-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.th-badge {
  margin-left: auto;
  font-family: var(--th-font-mono);
  font-size: 10px;
  color: var(--th-text-muted);
  background: var(--th-bg-hover);
  padding: 2px 8px;
  border-radius: 10px;
}

/* ═══════════════════════════════════════════════════════════════════════════
   FORM ELEMENTS
   ═══════════════════════════════════════════════════════════════════════════ */

.th-field-group {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.th-field {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &--primary { flex: 1; }
  &--secondary { width: 120px; }
}

.th-label {
  display: flex;
  align-items: center;
  gap: 4px;

  &__text {
    font-size: 11px;
    font-weight: 500;
    color: var(--th-text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__required {
    color: var(--th-error);
    font-size: 12px;
  }
}

.th-input-wrapper {
  position: relative;
}

/* Vuetify Input Overrides */
#addAutoTagsModal .th-input.v-text-field--outlined .v-input__slot,
#addAutoTagsModal .th-input.v-select--outlined .v-input__slot {
  background: var(--th-bg-elevated);
  min-height: 38px;
  border-radius: var(--th-radius-sm);
  border: 1px solid var(--th-border-default);
}

#addAutoTagsModal .th-input.v-text-field--outlined fieldset,
#addAutoTagsModal .th-input.v-select--outlined fieldset {
  border-color: var(--th-border-default);
  border-width: 1px;
}

#addAutoTagsModal .th-input.v-text-field--outlined:hover fieldset,
#addAutoTagsModal .th-input.v-select--outlined:hover fieldset {
  border-color: var(--th-border-strong);
}

#addAutoTagsModal .th-input.v-input--is-focused fieldset {
  border-color: var(--th-accent);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--th-accent) 8%, transparent);
}

#addAutoTagsModal .th-input input,
#addAutoTagsModal .th-input .v-select__selection {
  font-family: var(--th-font-primary);
  font-size: 13px;
  color: var(--th-text-primary);
}

#addAutoTagsModal .th-input input::placeholder {
  color: var(--th-text-muted);
}

#addAutoTagsModal .th-input .v-input__append-inner {
  margin-top: 0;
  align-self: center;
}

#addAutoTagsModal .th-input--mini.v-text-field--outlined .v-input__slot,
#addAutoTagsModal .th-input--mini.v-select--outlined .v-input__slot {
  min-height: 32px;
  padding: 0 8px;
}

#addAutoTagsModal .th-input--mini input,
#addAutoTagsModal .th-input--mini .v-select__selection {
  font-size: 12px;
}

#addAutoTagsModal .th-input--search.v-text-field--outlined .v-input__slot {
  min-height: 34px;
  padding-right: 8px;
}

.th-readonly {
  font-size: 12px;
  color: var(--th-text-primary);
  padding: 6px 0;

  &--muted {
    color: var(--th-text-muted);
    font-style: italic;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   FILTER CARDS - Card-Based Layout
   ═══════════════════════════════════════════════════════════════════════════ */

#addAutoTagsModal .th-filter-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

#addAutoTagsModal .th-filter-card {
  background: var(--th-bg-elevated);
  border: 1px solid var(--th-border-subtle);
  border-radius: var(--th-radius-md);
  padding: 12px;
  animation: th-card-enter 0.25s ease-out backwards;
  animation-delay: calc(var(--index) * 0.08s);
  transition: border-color var(--th-transition-fast), box-shadow var(--th-transition-fast);
}

#addAutoTagsModal .th-filter-card:hover {
  border-color: color-mix(in srgb, var(--th-accent) 20%, transparent);
  box-shadow: 0 2px 8px color-mix(in srgb, var(--th-accent) 8%, transparent);
}

@keyframes th-card-enter {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

#addAutoTagsModal .th-filter-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px dashed var(--th-border-subtle);
}

#addAutoTagsModal .th-filter-card__op {
  width: 90px;
  flex-shrink: 0;
}

#addAutoTagsModal .th-op-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
  color: var(--th-accent);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: 4px;
}

#addAutoTagsModal .th-filter-card__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

#addAutoTagsModal .th-filter-card__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

#addAutoTagsModal .th-filter-card__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

#addAutoTagsModal .th-filter-field {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 5px;
}

#addAutoTagsModal .th-filter-field--value {
  flex: 1;
}

#addAutoTagsModal .th-filter-field__label {
  font-size: 10px;
  font-weight: 600;
  color: var(--th-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

#addAutoTagsModal .th-input--op.v-select--outlined .v-input__slot {
  min-height: 30px;
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
}

#addAutoTagsModal .th-input--op .v-select__selection {
  color: var(--th-accent);
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
}

#addAutoTagsModal .th-readonly--any {
  font-size: 12px;
  color: var(--th-text-muted);
  font-style: italic;
  padding: 8px 0;
}

#addAutoTagsModal .th-value-input {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

#addAutoTagsModal .th-case-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border: 1px solid var(--th-border-default);
  background: var(--th-bg-elevated);
  border-radius: var(--th-radius-sm);
  font-family: var(--th-font-mono);
  font-size: 11px;
  font-weight: 500;
  color: var(--th-text-muted);
  cursor: pointer;
  transition: all var(--th-transition-fast);
}

#addAutoTagsModal .th-case-btn:hover:not(:disabled) {
  border-color: var(--th-accent);
  color: var(--th-accent);
}

#addAutoTagsModal .th-case-btn--active {
  background: var(--th-accent);
  border-color: var(--th-accent);
  color: white;
}

#addAutoTagsModal .th-case-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

#addAutoTagsModal .th-action-btn {
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--th-transition-fast);
}

#addAutoTagsModal .th-action-btn--add {
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
  color: var(--th-accent);
}

#addAutoTagsModal .th-action-btn--add:hover {
  background: var(--th-accent);
  color: white;
}

#addAutoTagsModal .th-action-btn--remove {
  background: transparent;
  color: var(--th-text-muted);
}

#addAutoTagsModal .th-action-btn--remove:hover {
  background: rgba(239, 68, 68, 0.1);
  color: var(--th-error);
}

#addAutoTagsModal .th-action-btn--collapse {
  background: var(--th-bg-hover);
  color: var(--th-text-secondary);
}

#addAutoTagsModal .th-action-btn--collapse:hover {
  background: var(--th-border-default);
  color: var(--th-text-primary);
}

/* Collapsed Filter Card State */
#addAutoTagsModal .th-filter-card--collapsed {
  padding: 10px 12px;
}

#addAutoTagsModal .th-filter-card--collapsed .th-filter-card__header {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

/* Summary Chips */
#addAutoTagsModal .th-filter-card__summary {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  flex-wrap: wrap;
  margin-left: 10px;
  overflow: hidden;
}

#addAutoTagsModal .th-summary-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: var(--th-bg-sunken);
  border: 1px solid var(--th-border-subtle);
  border-radius: 12px;
  font-size: 10px;
  white-space: nowrap;
  max-width: 120px;
  overflow: hidden;
}

#addAutoTagsModal .th-summary-chip__label {
  color: var(--th-text-muted);
  font-weight: 500;
}

#addAutoTagsModal .th-summary-chip__value {
  color: var(--th-text-primary);
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ═══════════════════════════════════════════════════════════════════════════
   SEARCH GRID
   ═══════════════════════════════════════════════════════════════════════════ */

.th-search-grid {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 12px 16px;
}

.th-search-master {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.th-master-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.th-master-icon {
  width: 24px;
  height: 24px;
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
  border-radius: var(--th-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;

  .v-icon {
    color: var(--th-accent);
  }
}

#addAutoTagsModal .th-search-stats {
  display: flex;
  gap: 8px;
  align-self: flex-end;
}

#addAutoTagsModal .th-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background: var(--th-bg-sunken);
  border-radius: var(--th-radius-sm);
  border: 1px solid var(--th-border-subtle);
  min-width: 70px;
}

#addAutoTagsModal .th-stat__value {
  font-family: var(--th-font-mono);
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
  display: block;
}

#addAutoTagsModal .th-stat__label {
  font-size: 9px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--th-text-muted);
  margin-top: 4px;
  display: block;
}

#addAutoTagsModal .th-stat--success .th-stat__value {
  color: var(--th-success);
}

#addAutoTagsModal .th-stat--warning .th-stat__value {
  color: var(--th-error);
}

/* ═══════════════════════════════════════════════════════════════════════════
   SEARCH OPTIONS - Toggle Switch Cards
   ═══════════════════════════════════════════════════════════════════════════ */

#addAutoTagsModal .th-search-options {
  grid-column: 1 / -1;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px;
  background: var(--th-bg-sunken);
  border: 1px solid var(--th-border-subtle);
  border-radius: var(--th-radius-md);
  margin-top: 4px;
}

#addAutoTagsModal .th-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  background: var(--th-bg-elevated);
  border: 1px solid var(--th-border-subtle);
  border-radius: var(--th-radius-sm);
  cursor: pointer;
  transition: all var(--th-transition-fast);
  position: relative;
  overflow: hidden;
  flex: 1;
}

#addAutoTagsModal .th-checkbox::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
  transition: background var(--th-transition-fast);
}

#addAutoTagsModal .th-checkbox:hover {
  border-color: var(--th-border-strong);
  background: var(--th-bg-hover);
}

#addAutoTagsModal .th-checkbox:hover::before {
  background: color-mix(in srgb, var(--th-accent) 20%, transparent);
}

/* Hide native checkbox */
#addAutoTagsModal .th-checkbox input {
  display: none;
}

/* Toggle Switch Style */
#addAutoTagsModal .th-checkbox__box {
  width: 36px;
  height: 20px;
  background: var(--th-border-default);
  border: none;
  border-radius: 10px;
  position: relative;
  transition: all var(--th-transition-fast);
  flex-shrink: 0;
}

#addAutoTagsModal .th-checkbox__box::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transform: translateX(0);
  transition: transform var(--th-transition-fast), box-shadow var(--th-transition-fast);
  border: none;
}

/* Checked State */
#addAutoTagsModal .th-checkbox input:checked + .th-checkbox__box {
  background: var(--th-accent);
}

#addAutoTagsModal .th-checkbox input:checked + .th-checkbox__box::after {
  transform: translateX(16px);
  box-shadow: 0 1px 4px color-mix(in srgb, var(--th-accent) 20%, transparent);
}

/* Active card styling when checked */
#addAutoTagsModal .th-checkbox:has(input:checked) {
  border-color: color-mix(in srgb, var(--th-accent) 20%, transparent);
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
}

#addAutoTagsModal .th-checkbox:has(input:checked)::before {
  background: var(--th-accent);
}

#addAutoTagsModal .th-checkbox:has(input:checked) .th-checkbox__label {
  color: var(--th-text-primary);
}

/* Label */
#addAutoTagsModal .th-checkbox__label {
  font-size: 12px;
  font-weight: 500;
  color: var(--th-text-secondary);
  transition: color var(--th-transition-fast);
  white-space: nowrap;
}

/* Hover states */
#addAutoTagsModal .th-checkbox:hover .th-checkbox__box {
  background: var(--th-border-strong);
}

#addAutoTagsModal .th-checkbox:hover .th-checkbox__label {
  color: var(--th-text-primary);
}

#addAutoTagsModal .th-checkbox:has(input:checked):hover .th-checkbox__box {
  background: color-mix(in srgb, var(--th-accent) 85%, black);
}

/* Focus visible for accessibility */
#addAutoTagsModal .th-checkbox:focus-within {
  outline: 2px solid color-mix(in srgb, var(--th-accent) 20%, transparent);
  outline-offset: 2px;
}

/* Section label */
#addAutoTagsModal .th-search-options::before {
  content: 'Options';
  display: block;
  width: 100%;
  font-size: 10px;
  font-weight: 600;
  color: var(--th-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

/* Dark mode adjustments */
.theme--dark #addAutoTagsModal .th-checkbox {
  background: var(--th-bg-elevated);
  border-color: var(--th-border-subtle);
}

.theme--dark #addAutoTagsModal .th-checkbox:hover {
  background: var(--th-bg-hover);
}

.theme--dark #addAutoTagsModal .th-checkbox__box {
  background: var(--th-border-strong);
}

.theme--dark #addAutoTagsModal .th-checkbox__box::after {
  background: var(--th-bg-elevated);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

#addAutoTagsModal .th-btn--search {
  grid-column: 2;
  grid-row: 1;
  align-self: flex-end;
}

/* ═══════════════════════════════════════════════════════════════════════════
   MATCHING GRID
   ═══════════════════════════════════════════════════════════════════════════ */

.th-matching-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* ═══════════════════════════════════════════════════════════════════════════
   PREVIEW
   ═══════════════════════════════════════════════════════════════════════════ */

#addAutoTagsModal .th-panel--preview {
  background: transparent;
  border: none;
}

#addAutoTagsModal .th-preview-layout {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

#addAutoTagsModal .th-preview-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

#addAutoTagsModal .th-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

#addAutoTagsModal .th-preview-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--th-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

#addAutoTagsModal .th-preview-count {
  font-weight: 400;
  color: var(--th-text-muted);
}

#addAutoTagsModal .th-preview-search {
  width: 160px;
}

#addAutoTagsModal .th-preview-content {
  height: 240px;
  min-height: 240px;
  max-height: 240px;
  background: var(--th-bg-sunken);
  border: 1px solid var(--th-border-subtle);
  border-radius: var(--th-radius-md);
  overflow: visible;
  position: relative;
}

#addAutoTagsModal .th-tags-list {
  height: 100%;
  max-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
  display: block;
}

#addAutoTagsModal .th-tags-list::-webkit-scrollbar {
  width: 5px;
}

#addAutoTagsModal .th-tags-list::-webkit-scrollbar-track {
  background: var(--th-bg-base);
  border-radius: 3px;
}

#addAutoTagsModal .th-tags-list::-webkit-scrollbar-thumb {
  background: var(--th-border-strong);
  border-radius: 3px;
}

#addAutoTagsModal .th-tags-list::-webkit-scrollbar-thumb:hover {
  background: var(--th-accent);
}

/* ═══════════════════════════════════════════════════════════════════════════
   FORGECARD - Compact Data Row Design
   ═══════════════════════════════════════════════════════════════════════════ */

/* Base card - compact single row */
#addAutoTagsModal .th-tags-list .th-tag-card.card-row,
#addAutoTagsModal .th-tags-list .card-row {
  position: relative;
  width: 100%;
  height: 44px;
  margin: 0 0 4px 0;
  padding: 0;
  background: var(--th-bg-elevated);
  border: 1px solid var(--th-border-subtle);
  border-left: 3px solid transparent;
  border-radius: var(--th-radius-sm);
  box-shadow: none;
  cursor: pointer;
  overflow: hidden;
  transition: all var(--th-transition-fast);
}

/* Hover state */
#addAutoTagsModal .th-tags-list .th-tag-card.card-row:hover,
#addAutoTagsModal .th-tags-list .card-row:hover {
  border-left-color: var(--th-accent);
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
  box-shadow: var(--th-shadow-xs);
}

/* Selected/Highlighted state */
#addAutoTagsModal .th-tags-list .th-tag-card.card-row.selected-card,
#addAutoTagsModal .th-tags-list .card-row.selected-card {
  border-color: var(--th-accent);
  border-left-color: var(--th-accent);
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--th-accent) 20%, transparent);
}

/* Last card spacing */
#addAutoTagsModal .th-tags-list .th-tag-card.card-row:last-child,
#addAutoTagsModal .th-tags-list .card-row:last-child {
  margin-bottom: 0;
}

/* Hide card image completely */
#addAutoTagsModal .th-tags-list .card-row .card-img {
  display: none !important;
}

/* Card content - horizontal layout */
#addAutoTagsModal .th-tags-list .card-row .card-content {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 12px;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* Card title - primary info */
#addAutoTagsModal .th-tags-list .card-row .card-title {
  font-family: var(--th-font-primary);
  font-size: 12px;
  font-weight: 600;
  color: var(--th-text-primary);
  margin: 0;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: auto;
  line-height: 1;
}

#addAutoTagsModal .th-tags-list .card-row:hover .card-title {
  color: var(--th-accent);
}

/* Hide subtitles in compact mode */
#addAutoTagsModal .th-tags-list .card-row .card-sub-title {
  display: none;
}

/* Card bottom bar - inline with content */
#addAutoTagsModal .th-tags-list .card-row .card-bottom-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: auto;
}

/* Category chip - compact */
#addAutoTagsModal .th-tags-list .card-row .card-element-category .v-chip {
  height: 20px;
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  background: color-mix(in srgb, var(--th-accent) 8%, transparent) !important;
  color: var(--th-accent) !important;
  border: none;
  padding: 0 8px;
  border-radius: 10px;
}

#addAutoTagsModal .th-tags-list .card-row .card-element-category .v-chip .v-chip__content {
  padding: 0;
}

/* Category circle */
#addAutoTagsModal .th-tags-list .card-row .category-circle {
  display: none;
}

/* Matched search text - inline pill */
#addAutoTagsModal .th-tags-list .card-row .search-text {
  font-family: var(--th-font-mono);
  font-size: 10px;
  font-weight: 500;
  color: var(--th-success);
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
  padding: 2px 8px;
  border-radius: 10px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Hide watermark in compact mode - or make it minimal */
#addAutoTagsModal .th-tags-list .card-row .watermark-item {
  display: none;
}

/* Hide buttons row */
#addAutoTagsModal .th-tags-list .card-row .buttons-row {
  display: none;
}

/* Checkbox styling - centered */
#addAutoTagsModal .th-tags-list .card-row .checkbox-inside {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 10px;
  width: 14px;
  height: 14px;
  accent-color: var(--th-accent);
}

/* No underline on hover */
#addAutoTagsModal .th-tags-list .card-row:hover div p {
  text-decoration: none;
}

/* Zebra striping for better readability */
#addAutoTagsModal .th-tags-list .card-row:nth-child(even) {
  background: var(--th-bg-sunken);
}

#addAutoTagsModal .th-tags-list .card-row:nth-child(even):hover {
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
}

/* Dark mode adjustments */
.theme--dark #addAutoTagsModal .th-tags-list .card-row {
  background: var(--th-bg-elevated);
  border-color: var(--th-border-subtle);
}

.theme--dark #addAutoTagsModal .th-tags-list .card-row:nth-child(even) {
  background: var(--th-bg-sunken);
}

.theme--dark #addAutoTagsModal .th-tags-list .card-row:hover,
.theme--dark #addAutoTagsModal .th-tags-list .card-row:nth-child(even):hover {
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
}

#addAutoTagsModal .th-empty {
  height: 100%;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--th-text-muted);
  padding: 20px;
}

#addAutoTagsModal .th-empty__icon svg {
  width: 44px;
  height: 44px;
  opacity: 0.35;
  stroke: var(--th-text-muted);
}

#addAutoTagsModal .th-empty__text {
  font-size: 13px;
  font-weight: 500;
  color: var(--th-text-muted);
}

#addAutoTagsModal .th-preview-tree {
  display: none; /* Hidden for now per user request */
}

/* ═══════════════════════════════════════════════════════════════════════════
   BUTTONS
   ═══════════════════════════════════════════════════════════════════════════ */

#addAutoTagsModal .th-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  height: 38px;
  border: none;
  border-radius: var(--th-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--th-transition-fast);
}

#addAutoTagsModal .th-btn--primary {
  background: var(--th-accent);
  color: white;
}

#addAutoTagsModal .th-btn--primary:hover:not(:disabled) {
  background: color-mix(in srgb, var(--th-accent) 85%, black);
}

#addAutoTagsModal .th-btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

#addAutoTagsModal .th-btn--ghost {
  background: transparent;
  color: var(--th-text-secondary);
  border: 1px solid var(--th-border-default);
}

#addAutoTagsModal .th-btn--ghost:hover {
  background: var(--th-bg-hover);
  border-color: var(--th-border-strong);
}

#addAutoTagsModal .th-btn--outline {
  background: transparent;
  color: var(--th-text-secondary);
  border: 1px solid var(--th-border-default);
}

#addAutoTagsModal .th-btn--outline:hover {
  background: var(--th-bg-hover);
  color: var(--th-text-primary);
}

#addAutoTagsModal .th-btn--icon {
  width: 38px;
  padding: 0;
}

#addAutoTagsModal .th-btn--add {
  background: color-mix(in srgb, var(--th-accent) 8%, transparent);
  color: var(--th-accent);
  border: 1px dashed var(--th-accent);
}

#addAutoTagsModal .th-btn--add:hover {
  background: var(--th-accent);
  color: white;
  border-style: solid;
}

#addAutoTagsModal .th-btn__text {
  white-space: nowrap;
}

#addAutoTagsModal .th-btn__icon svg {
  width: 16px;
  height: 16px;
}

#addAutoTagsModal .th-btn__arrow {
  width: 16px;
  height: 16px;
  transition: transform var(--th-transition-fast);
}

#addAutoTagsModal .th-btn:hover .th-btn__arrow {
  transform: translateX(3px);
}

/* ═══════════════════════════════════════════════════════════════════════════
   FOOTER
   ═══════════════════════════════════════════════════════════════════════════ */

#addAutoTagsModal .th-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  background: var(--th-bg-sunken);
  border-top: 1px solid var(--th-border-subtle);
}

/* ═══════════════════════════════════════════════════════════════════════════
   LOADING
   ═══════════════════════════════════════════════════════════════════════════ */

.th-loading {
  position: absolute;
  inset: 0;
  background: var(--th-bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;
  z-index: 20;
}

.th-loading__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.th-loading__spinner {
  width: 48px;
  height: 48px;
  color: var(--th-accent);

  svg {
    width: 100%;
    height: 100%;
    animation: th-spin 1s linear infinite;
  }
}

@keyframes th-spin {
  to { transform: rotate(360deg); }
}

.th-loading__text {
  font-family: var(--th-font-mono);
  font-size: 12px;
  color: var(--th-text-muted);
  letter-spacing: 0.02em;
}

/* ═══════════════════════════════════════════════════════════════════════════
   CONTEXT MENU
   ═══════════════════════════════════════════════════════════════════════════ */

.th-context-menu {
  list-style: none;
  margin: 0;
  padding: 6px 0;

  li {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    font-size: 13px;
    color: var(--th-text-primary);
    cursor: pointer;
    transition: background var(--th-transition-fast);

    &:hover {
      background: var(--th-bg-hover);
    }

    .v-icon {
      color: var(--th-error);
    }
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   RESPONSIVE
   ═══════════════════════════════════════════════════════════════════════════ */

@media (max-width: 560px) {
  .th-dialog.modal-content {
    width: calc(100% - 24px);
    max-height: calc(100vh - 32px);
  }

  .th-field-group {
    flex-wrap: wrap;
  }

  .th-field--secondary {
    width: 100%;
  }

  .th-filters-header,
  .th-filter-row {
    grid-template-columns: 60px 1fr 1fr 80px 1fr 40px;
    gap: 4px;
  }

  .th-search-grid {
    grid-template-columns: 1fr;
  }

  .th-btn--search {
    grid-column: 1;
    grid-row: auto;
  }

  .th-matching-grid {
    grid-template-columns: 1fr;
  }

  .th-preview-layout {
    flex-direction: column;
  }
}
</style>
